/**
 * AdminLayout -- layout wrapper for /admin/* pages.
 *
 * Renders the admin sidebar on the left and the main content area on the right.
 * Includes a breadcrumb at the top of the content area.
 *
 * Desktop: sidebar with resize handle (220-380px, default 260px).
 * Mobile: drawer with backdrop (width: min(85vw, 320px)), close button X.
 *
 * Container: max-w-[1440px] px-4 py-8.
 */

import { Suspense, useState, useCallback } from 'react';
import { Outlet } from 'react-router';
import { useIntl } from 'react-intl';
import { List, X } from '@phosphor-icons/react';
import { AdminSidebar } from '@/components/navigation/AdminSidebar';
import { AdminBreadcrumb } from '@/components/navigation/AdminBreadcrumb';
import { AppTopBar } from '@/components/layout/AppTopBar';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

// ---------------------------------------------------------------------------
// Loading fallback for lazy-loaded admin pages
// ---------------------------------------------------------------------------

function AdminPageFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminLayout() {
  const intl = useIntl();
  const [mobileOpen, setMobileOpen] = useState(false);

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return (
    <div className="flex min-h-screen flex-col bg-bg-primary text-text-primary">
      <div className="flex flex-1">
        {/* Desktop sidebar -- sticky so it stays visible while body scrolls */}
        <div className="hidden md:flex sticky top-0 h-screen">
          <AdminSidebar />
        </div>

        {/* Mobile backdrop (design.md C.6) */}
        {mobileOpen && (
          <div
            className={cn(
              'fixed inset-0 z-[var(--z-overlay,40)] bg-bg-overlay md:hidden',
            )}
            onClick={closeMobile}
            aria-hidden="true"
          />
        )}

        {/* Mobile drawer (design.md C.6) */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-[calc(var(--z-overlay,40)+1)]',
            'w-[min(85vw,320px)]',
            'transform transition-transform duration-200',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
            'md:hidden',
          )}
        >
          <AdminSidebar />
          <button
            type="button"
            onClick={closeMobile}
            className="absolute top-3 right-2 z-10 rounded-md p-1.5 text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary"
            aria-label={intl.formatMessage({ id: 'a11y.closeMenu' })}
          >
            <X size={20} />
          </button>
        </aside>

        {/* Main content area */}
        <div className="flex flex-1 flex-col min-w-0">
          {/* Top bar: hamburger (mobile) + breadcrumb + AppTopBar right actions */}
          <div className="sticky top-0 z-20">
            <AppTopBar
              left={
                <div className="flex min-w-0 items-center gap-3"> {/* FEAT-052 TASK-013 */}
                  {/* Mobile hamburger */}
                  <button
                    type="button"
                    onClick={openMobile}
                    className="rounded-md p-1.5 text-text-secondary hover:bg-bg-tertiary hover:text-text-primary md:hidden shrink-0"
                    aria-label={intl.formatMessage({ id: 'a11y.openMenu' })}
                  >
                    <List size={20} />
                  </button>
                  <AdminBreadcrumb className="flex-1 min-w-0" />
                </div>
              }
            />
          </div>

          {/* Page content -- no overflow-y-auto; body scroll takes over */}
          <main className="flex-1">
            <div className="mx-auto max-w-[1440px] px-4 py-8 sm:px-6 lg:px-8 animate-fade-in">
              <Suspense fallback={<AdminPageFallback />}>
                <Outlet />
              </Suspense>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
